'use client'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

// API lấy danh sách ngân hàng từ VietQR
export interface IBank {
  id: number
  name: string
  code: string
  bin: string
  shortName: string
  logo: string
  transferSupported: number
  lookupSupported: number
  short_name: string
  support: number
  isTransfer: number
  swift_code: string
}

export interface BankListResponse {
  code: string
  desc: string
  data: IBank[]
}

/**
 * Lấy danh sách ngân hàng từ API VietQR
 * @returns Promise<BankListResponse>
 */
async function getBanksAction(): Promise<BankListResponse> {
  try {
    const response = await fetch('https://api.vietqr.io/v2/banks', {
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch banks: ${response.status} ${response.statusText}`)
    }

    const data = (await response.json()) as BankListResponse
    return data
  } catch (error) {
    console.error('Error fetching banks:', error)
    throw error
  }
}

/**
 * Hook để lấy danh sách ngân hàng từ VietQR API
 * @returns {Object} banks, bankOptions, isLoading, error
 */
export const useBanks = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['BANKS_LIST'],
    queryFn: getBanksAction,
    staleTime: 1000 * 60 * 60, // Cache 1 giờ (danh sách bank ít thay đổi)
    gcTime: 1000 * 60 * 60 * 24, // Giữ trong cache 24 giờ
    retry: 2, // Retry 2 lần khi fail
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    refetchOnWindowFocus: false, // Không refetch khi focus window
    refetchOnMount: false, // Không refetch khi mount lại (vì data ít thay đổi)
  })

  // Memoize sorted banks để tránh re-sort không cần thiết
  const sortedBanks = useMemo(() => {
    if (!data?.data) return []
    return [...data.data].sort((a, b) => {
      return (a.code || '').localeCompare(b.code || '')
    })
  }, [data?.data])

  // Memoize bank options để tránh re-map không cần thiết
  const bankOptions = useMemo(() => {
    return sortedBanks.map((bank) => ({
      label: bank.short_name || bank.shortName || bank.name,
      value: bank.short_name || bank.shortName || bank.code,
    }))
  }, [sortedBanks])

  return {
    banks: data?.data || [],
    sortedBanks,
    bankOptions,
    isLoading,
    error,
  }
}
